/**
 * Zombie→Human contact conversion. Each tick, queries the `human` layer for
 * any neighbour within `b.r * 2 + margin` and calls `die()` on every hit —
 * `Human.die` spawns a fresh Zombie in its place (reusing the dying human's
 * `id` slot). The `minAgeBeforeConvert` gate skips freshly-spawned zombies
 * (paired with `Zombie.tick`'s own age check, this enforces the 3-second
 * stun window during which a zombie cannot convert anyone).
 */
import { Boid } from '../boids/Boid';
import { IGameTime } from '../GameClock';
import { vec2 } from '../math';
import { BoidBehavior, IBehaviorOptions } from './BoidBehavior';

export interface IConvertHumanBehaviorOptions extends IBehaviorOptions {
  margin: number;
  minAgeBeforeConvert: number;
}

export class ConvertHumanBehavior<T extends Boid> extends BoidBehavior<T> {
  layerId: number;
  margin: number;
  minAgeBeforeConvert: number;

  constructor(boid: T, scale: number, options: IConvertHumanBehaviorOptions) {
    super(boid, 'ConvertHumanBehavior', scale, options);

    this.layerId = boid.World.layerByName('human');
    this.margin = options.margin;
    this.minAgeBeforeConvert = options.minAgeBeforeConvert;
  }

  public override tick(_gameTime: IGameTime): void {
    if (!this.enabled) return;

    const b = this.boid;
    if (b.age < this.minAgeBeforeConvert) return;

    const p: vec2 = b.p;
    const grid = b.options.grid;
    const nearest = grid.getDataRadius(p.x, p.y, b.r * 2 + this.margin, true, b, true, this.layerId);
    if (!nearest.length) return;
    for (const na of nearest) {
      na.data.die();
    }
  }
}
